import {
  ArrowLeft,
  Github,
  ExternalLink,
  Rocket,
  Trophy,
} from "lucide-react";
import React, { useEffect, useMemo } from "react";
import { hackathons, selectedOpenSourceWork } from "../data/content";
import { trackLinkClick, trackPageView } from "../utils/analytics";
import { Card, CardContent } from "./ui/card";

const GITHUB_USERNAME = "markballew";

const EmptyStateCard = ({ title, description, codeRef }) => {
  return (
    <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm">
      <CardContent className="p-6">
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <p className="text-gray-700 text-sm leading-relaxed">{description}</p>
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-3">
            <p className="text-xs text-gray-600">
              Add entries in{" "}
              <code className="bg-white/70 px-2 py-1 rounded">{codeRef}</code>
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const HackathonCard = ({ item }) => {
  const awardBadges = useMemo(() => {
    if (!item.award) return [];
    if (Array.isArray(item.award)) return item.award.filter(Boolean);
    return [item.award];
  }, [item.award]);

  return (
    <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm hover:-translate-y-1 transition-transform duration-300">
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-b from-yellow-50 to-yellow-100">
                <Trophy className="w-5 h-5 text-yellow-700" />
              </div>
              <div>
                <h3 className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  {item.event}
                </h3>
                <p className="text-sm text-gray-600">
                  {item.project}
                  {item.year ? ` • ${item.year}` : ""}
                </p>
              </div>
            </div>
            {!!awardBadges.length && (
              <div className="flex flex-wrap gap-2 justify-end">
                {awardBadges.map((award) => (
                  <span
                    key={award}
                    className="bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium"
                  >
                    {award}
                  </span>
                ))}
              </div>
            )}
          </div>

          {item.description && (
            <p className="text-gray-700 leading-relaxed text-sm">
              {item.description}
            </p>
          )}

          {!!item.technologies?.length && (
            <div className="flex flex-wrap gap-2">
              {item.technologies.map((tech) => (
                <span
                  key={tech}
                  className="bg-gradient-to-r from-blue-50 to-purple-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium"
                >
                  {tech}
                </span>
              ))}
            </div>
          )}

          {!!item.links?.length && (
            <div className="flex flex-wrap gap-3 pt-1">
              {item.links.map((link) => (
                <a
                  key={link.label}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-1"
                  onClick={() => trackLinkClick(link.label, link.url)}
                >
                  {link.label} <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const SelectedWorkCard = ({ item, index }) => {
  return (
    <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm hover:-translate-y-1 transition-transform duration-300">
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-b from-blue-50 to-purple-100">
                <Trophy className="w-5 h-5 text-indigo-700" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  [{index + 1}] {item.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {item.venue}
                  {item.year ? ` • ${item.year}` : ""}
                  {item.role ? ` • ${item.role}` : ""}
                </p>
              </div>
            </div>
            {item.type && (
              <span className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
                {item.type}
              </span>
            )}
          </div>

          {item.description && (
            <p className="text-gray-700 leading-relaxed text-sm">
              {item.description}
            </p>
          )}

          {!!item.tags?.length && (
            <div className="flex flex-wrap gap-2">
              {item.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-gradient-to-r from-blue-50 to-purple-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {item.link && (
            <div className="pt-1">
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-1"
                onClick={() => trackLinkClick(item.title, item.link)}
              >
                Reference <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const FeaturedProjectCard = ({ item }) => {
  if (!item) return null;

  return (
    <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm overflow-hidden">
      <CardContent className="p-8">
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-3 rounded-lg bg-gradient-to-b from-blue-50 to-purple-100">
                <Trophy className="w-6 h-6 text-indigo-700" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  Featured: zkx402 implementation
                </h2>
                <p className="text-sm text-gray-600 mt-1">{item.title}</p>
              </div>
            </div>
            {item.type && (
              <span className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
                {item.type}
              </span>
            )}
          </div>

          {item.description && (
            <p className="text-gray-700 leading-relaxed">{item.description}</p>
          )}

          {!!item.tags?.length && (
            <div className="flex flex-wrap gap-2">
              {item.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-gradient-to-r from-blue-50 to-purple-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {item.link && (
            <div className="pt-1">
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-1"
                onClick={() => trackLinkClick(item.title, item.link)}
              >
                View repository <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const AccomplishmentsPage = ({ showBackToHome = true, onBack }) => {
  useEffect(() => {
    trackPageView("accomplishments");
  }, []);

  const hasHackathons = Array.isArray(hackathons) && hackathons.length > 0;
  const hasSelectedWork =
    Array.isArray(selectedOpenSourceWork) && selectedOpenSourceWork.length > 0;
  const featuredZkx402 =
    selectedOpenSourceWork?.find?.((x) => x?.id === "vhspace-zkx402") || null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="py-16 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          {showBackToHome && (
            <div className="flex justify-center mb-8">
              {onBack ? (
                <button
                  onClick={onBack}
                  className="inline-flex items-center gap-2 bg-white/70 text-gray-700 px-4 py-2 rounded-lg hover:scale-105 transition-transform duration-300 font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
              ) : (
                <a
                  href="/"
                  className="inline-flex items-center gap-2 bg-white/70 text-gray-700 px-4 py-2 rounded-lg hover:scale-105 transition-transform duration-300 font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Home
                </a>
              )}
            </div>
          )}

          <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">
            Tech Accomplishments
          </h1>
          <p className="text-lg text-gray-600">
            Hackathons and a rolling index of recent public repositories
          </p>

          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <a
              href={`https://github.com/${GITHUB_USERNAME}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800 px-5 py-2 rounded-lg hover:scale-105 transition-transform duration-300 font-medium"
              onClick={() =>
                trackLinkClick(
                  "GitHub Profile",
                  `https://github.com/${GITHUB_USERNAME}`
                )
              }
            >
              <Github className="w-4 h-4" />
              GitHub Profile
            </a>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 pb-20 space-y-12">
        {/* Featured project (very top) */}
        <section>
          <FeaturedProjectCard item={featuredZkx402} />
        </section>

        {/* Hackathons (top) */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Rocket className="w-6 h-6 text-orange-600" />
            <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Hackathon Projects
            </h2>
          </div>

          {hasHackathons ? (
            <div className="grid md:grid-cols-2 gap-6">
              {hackathons.map((item) => (
                <HackathonCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <EmptyStateCard
              title="No hackathons listed yet"
              description="Once you add a few hackathon entries, they’ll show up here automatically."
              codeRef="/src/data/content.js"
            />
          )}
        </section>

        {/* Selected open-source work (keep pp2p + inspect-action) */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Trophy className="w-6 h-6 text-indigo-600" />
            <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Selected Open-Source Work
            </h2>
          </div>

          {hasSelectedWork ? (
            <div className="grid md:grid-cols-2 gap-6">
              {selectedOpenSourceWork.map((item, idx) => (
                <SelectedWorkCard key={item.id} item={item} index={idx} />
              ))}
            </div>
          ) : (
            <EmptyStateCard
              title="No selected work listed yet"
              description="Add a few key repos or contributions you want to highlight."
              codeRef="/src/data/content.js"
            />
          )}
        </section>
      </main>

      <footer className="text-center py-8 text-gray-600">
        <p>&copy; 2024 Mark Ballew</p>
      </footer>
    </div>
  );
};

export default AccomplishmentsPage;


